/**
 * inkDropPipeline — falling ink drop with ripples.
 *
 * A black ink drop falls from the top, enters the water surface,
 * and creates expanding ripples. The whole sequence repeats on a
 * loop. Audio drives ripple intensity and speed.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface InkDropPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected coords, Y up (so the drop falls down).
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.y = -p.y;

  // Loop period: 3 seconds per drop.
  float cycle = mod(uTime, 3.0);
  // Drop falls from y = 1.0 to y = -0.3 over the first 1.5 seconds.
  float dropY = mix(1.0, -0.3, cycle / 1.5);
  // Drop shape: ellipse that stretches as it falls.
  float dropStretch = 1.0 + 2.0 * smoothstep(0.5, 1.0, cycle / 1.5);
  // Audio: stretch more with bass.
  dropStretch *= 1.0 + bass * 0.5;
  // Drop position: sin oscillation in X (slight wobble).
  float dropX = sin(cycle * 4.0) * 0.05;
  // Distance from current pixel to drop.
  vec2 dropPos = vec2(dropX, dropY);
  float dropDist = length((p - dropPos) / vec2(0.05, 0.08 / dropStretch));

  // Drop opacity (fades at the end of its fall).
  float dropAlpha = smoothstep(1.0, 0.95, dropDist) * (1.0 - smoothstep(0.7, 1.0, cycle / 1.5));

  // Surface ripples: emanate from the impact point after the drop hits.
  float impactPhase = cycle - 1.0;
  float rippleAlpha = step(0.0, impactPhase) * smoothstep(0.0, 0.05, impactPhase) *
                      (1.0 - smoothstep(2.0, 3.0, cycle));
  // Ripples expand outward.
  float ripple1Radius = impactPhase * 0.8;
  float ripple2Radius = max(impactPhase - 0.3, 0.0) * 0.6;
  float ripple3Radius = max(impactPhase - 0.6, 0.0) * 0.4;
  float rippleDist = abs(length(p) - ripple1Radius) +
                     abs(length(p) - ripple2Radius) +
                     abs(length(p) - ripple3Radius);
  float ripples = smoothstep(0.04, 0.0, rippleDist) * rippleAlpha;
  // Audio: ripple intensity.
  ripples *= 1.0 + mid * 1.5;

  // Background: light paper / water surface.
  vec3 bg = vec3(0.94, 0.93, 0.88);
  // Subtle water texture.
  float bgNoise = fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
  bg -= 0.02 * bgNoise;

  // Apply drop.
  vec3 dropColor = vec3(0.05, 0.08, 0.20);
  vec3 col = mix(bg, dropColor, dropAlpha);
  // Apply ripples.
  col = mix(col, vec3(0.30, 0.40, 0.55), ripples * 0.7);

  // Add a faint shadow under the drop (slightly larger ellipse below).
  float shadowDist = length((p - vec2(dropX, -0.32)) / vec2(0.10, 0.02));
  float shadow = smoothstep(1.0, 0.0, shadowDist) * 0.15 * step(0.0, impactPhase);
  col = mix(col, vec3(0.6), shadow);

  // Audio: subtle ripple brightness on bass.
  col *= 1.0 - bass * 0.05;

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = col / (1.0 + col);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 1.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeInkDropBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeInkDropRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createInkDropPipeline(gl: WebGL2RenderingContext): InkDropPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawInkDrop(
  gl: WebGL2RenderingContext,
  pipe: InkDropPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
